import { Button, Modal, TextInput } from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type FlowStep,
  useConnectedAccountsFlow,
} from '@/components/ConnectedAccounts/useConnectedAccountsFlow';
import { LedgerRow } from '@/components/Ui/LedgerRow';
import { Section } from '@/components/Ui/Section';
import { useLanguageAwareRouting } from '@/hooks/useLanguageAwareRouting';
import { markAccountDeleted } from '@/lib/account-deleted';
import { downloadText } from '@/lib/messages';
import { useRustyState } from '@/state';
import shared from '../AccountScreens.module.css';
import classes from './Security.module.css';

const CONFIRM_WORD = 'DELETE';

export function DataPrivacy() {
  const { t } = useTranslation();
  const { createLanguageURL } = useLanguageAwareRouting();
  const api = useRustyState.getState().api;
  const formCount = useRustyState((s) => s.formsSummary?.length ?? null);
  const flow = useConnectedAccountsFlow(api);

  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(false);
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteInfo, setDeleteInfo] = useState<string | null>(null);
  const [nostrSign, setNostrSign] = useState<(() => Promise<FlowStep>) | null>(null);

  const exportData = async () => {
    setExporting(true);
    setExportError(false);
    try {
      const zip = await api.exportAccountData();
      downloadText(
        zip,
        `formshive-export-${new Date().toISOString().slice(0, 10)}.zip`,
        'application/zip'
      );
    } catch {
      setExportError(true);
    } finally {
      setExporting(false);
    }
  };

  const finish = async () => {
    if (!(await useRustyState.getState().logout())) {
      window.location.replace(`#${createLanguageURL('/')}`);
    }
  };

  const handleStep = async (step: FlowStep) => {
    switch (step.kind) {
      case 'redirecting':
        return;
      case 'email_pending':
        setDeleteInfo(t('security.data.deleteCheckEmail'));
        setDeleting(false);
        return;
      case 'nostr_sign_required':
        setNostrSign(() => step.sign);
        setDeleting(false);
        return;
      case 'deleted':
        markAccountDeleted();
        // The server ended the session with the account; a logout call would only 401.
        await useRustyState.getState().clearLocalSession();
        window.location.replace(`#${createLanguageURL('/')}`);
        return;
      case 'completed':
        await finish();
        return;
      case 'error':
        setDeleteError(
          t(`connectedAccounts.error.${step.code}`, {
            defaultValue: t('security.data.deleteFailed'),
          })
        );
        setDeleting(false);
        return;
    }
  };

  const startDelete = async () => {
    if (typed !== CONFIRM_WORD || deleting) return;
    setDeleting(true);
    setDeleteError(null);
    setDeleteInfo(null);
    setNostrSign(null);
    try {
      await handleStep(await flow.startDelete(typed));
    } catch {
      setDeleteError(t('security.data.deleteFailed'));
      setDeleting(false);
    }
  };

  const signNostr = async () => {
    const sign = nostrSign;
    if (!sign || deleting) return;
    setDeleting(true);
    setNostrSign(null);
    try {
      await handleStep(await sign());
    } catch {
      setDeleteError(t('security.data.deleteFailed'));
      setDeleting(false);
    }
  };

  return (
    <Section id="data" title={t('security.data.title')}>
      <LedgerRow
        label={t('security.data.export')}
        value={<span>{t('security.data.exportValue')}</span>}
        help={t('security.data.exportHelp')}
        risk={exportError ? t('security.data.exportFailed') : undefined}
        action={
          <Button variant="default" size="xs" onClick={exportData} loading={exporting}>
            {t('security.data.exportButton')}
          </Button>
        }
      />
      <LedgerRow
        label={<span className={classes.deleteLabel}>{t('security.data.delete')}</span>}
        value={
          <span>
            {formCount !== null
              ? t('security.data.deleteValue', { count: formCount })
              : t('security.data.deleteValueNoCount')}
          </span>
        }
        help={t('security.data.deleteHelp')}
        action={
          <Button variant="danger" size="xs" onClick={() => setOpen(true)}>
            {t('security.data.deleteButton')}
          </Button>
        }
      />

      <Modal
        opened={open}
        onClose={() => {
          if (!deleting) setOpen(false);
        }}
        title={t('security.data.deleteTitle')}
      >
        <div className={shared.stack} style={{ gap: 14 }}>
          <span>{t('security.data.deleteBody')}</span>
          <TextInput
            label={t('security.data.deleteType', { word: CONFIRM_WORD })}
            value={typed}
            onChange={(e) => setTyped(e.currentTarget.value)}
            autoComplete="off"
            disabled={deleting}
            maw={240}
          />
          {deleteError && (
            <p className={shared.errorText} role="alert">
              {deleteError}
            </p>
          )}
          {deleteInfo && (
            <p className={shared.sub} role="status" style={{ margin: 0 }}>
              {deleteInfo}
            </p>
          )}
          <div className={shared.row}>
            {nostrSign ? (
              <Button variant="danger" onClick={signNostr} loading={deleting}>
                {t('security.methods.nostrSignButton')}
              </Button>
            ) : (
              <Button
                variant="danger"
                onClick={startDelete}
                loading={deleting}
                disabled={typed !== CONFIRM_WORD}
              >
                {t('security.data.deleteConfirm')}
              </Button>
            )}
            <Button variant="default" onClick={() => setOpen(false)} disabled={deleting}>
              {t('ui.cancel')}
            </Button>
          </div>
        </div>
      </Modal>
    </Section>
  );
}
